import { useState, useEffect } from "react";
import api from "../../api/axios";
import { 
  Sparkles, 
  TrendingUp, 
  Heart, 
  Map, 
  AlertCircle 
} from "lucide-react";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from "recharts";

/**
 * 👁️ AYI (Antaryatra Intelligence) - THE COMPASSION DASHBOARD
 * This is the heart of our mission. It identifies "Healing Gaps" and Community Heartbeat
 * without ever tracking individual souls.
 */

export default function AdminAYI() {
  const [data, setData] = useState({
    growth: [],
    funnel: [],
    gaps: [],
    mood: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAYIData() {
      try {
        setLoading(true);
        // Fetch Parallelly
        const [growth, funnel, gaps, mood] = await Promise.all([
          api.get('/analytics/pulse/growth'),
          api.get('/analytics/pulse/funnel'),
          api.get('/analytics/ayi/gaps'),
          api.get('/analytics/ayi/mood')
        ]);

        setData({
          growth: growth.data,
          funnel: funnel.data,
          gaps: gaps.data,
          mood: mood.data
        });
      } catch (err) {
        console.error("AYI Sensor Failure:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchAYIData();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-sky-400 font-mono tracking-widest animate-pulse">
        <Sparkles className="mb-4 w-12 h-12" />
        SENSING COMMUNITY RESONANCE...
      </div>
    );
  }

  return (
    <div className="space-y-12 pb-20">
      
      {/* Header (Mission Centric) */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-4xl font-bold text-white tracking-tight flex items-center gap-3">
            <Sparkles className="text-sky-400" />
            Antaryatra <span className="text-gray-500 font-light">Intelligence</span>
          </h2>
          <p className="text-gray-400 mt-2 max-w-lg">
            Identity-blind intelligence layer. We track <span className="text-sky-300">healing inertia</span>, not humans.
          </p>
        </div>

        <div className="flex gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10">
            <div className="text-center px-4">
                <div className="text-2xl font-bold text-sky-400 font-mono">
                    {data.growth[data.growth.length - 1]?.seekerCount || 0}
                </div>
                <div className="text-[10px] text-gray-500 uppercase tracking-widest">Active Seekers</div>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="text-center px-4">
                <div className="text-2xl font-bold text-emerald-400 font-mono">
                    {data.growth[data.growth.length - 1]?.providerCount || 0}
                </div>
                <div className="text-[10px] text-gray-500 uppercase tracking-widest">Trusted Guides</div>
            </div>
        </div>
      </section>

      {/* --- LAYER 1: HEALING GAPS (COMPASSION MAP) --- */}
      <section className="bg-white/[0.02] border border-white/10 rounded-3xl p-8">
        <div className="mb-8">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Map className="w-5 h-5 text-amber-500" />
                The Healing Gaps (Category Demand)
            </h3>
            <p className="text-sm text-gray-500 mt-1">Which voids are the loudest? We prioritize outreach based on these signals.</p>
        </div>

        <div className="h-80 w-full relative">
            {data.gaps.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.gaps}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                        <XAxis dataKey="category" stroke="#666" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis stroke="#666" fontSize={12} tickLine={false} axisLine={false} />
                        <Tooltip 
                            contentStyle={{ backgroundColor: '#000', border: '1px solid #333', color: '#fff' }}
                            itemStyle={{ color: '#fff' }}
                        />
                        <Bar dataKey="demandScore" radius={[6, 6, 0, 0]}>
                            {data.gaps.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.isUnmet ? '#f59e0b' : '#3b82f6'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <div className="h-full w-full flex flex-col items-center justify-center border-2 border-dashed border-white/5 rounded-2xl bg-white/[0.01]">
                    <Map className="w-10 h-10 text-gray-700 mb-4" />
                    <p className="text-gray-500 font-medium">Waiting for first seeker reflections...</p>
                    <p className="text-xs text-gray-600 mt-1 uppercase tracking-widest">Gaps will appear here</p>
                </div>
            )}
        </div>

        {data.gaps.some(g => g.isUnmet) && (
            <div className="mt-8 flex items-center gap-4 p-4 rounded-xl bg-amber-900/20 border border-amber-900/50">
                <AlertCircle className="text-amber-500 w-10 h-10 shrink-0" />
                <p className="text-sm text-amber-200">
                    <span className="font-bold">CRITICAL GAP:</span> High resonance detected in <span className="underline italic">unmet</span> categories. We need to recruit more specialized guides in these areas immediately.
                </p>
            </div>
        )}
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        
        {/* --- LAYER 2: GROWTH MOMENTUM --- */}
        <section className="bg-white/[0.02] border border-white/10 rounded-3xl p-8">
            <div className="mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-sky-400" />
                    Sanctuary Growth
                </h3>
            </div>
            <div className="h-64 relative">
                {data.growth.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.growth}>
                            <defs>
                                <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="date" hide />
                            <YAxis hide />
                            <Tooltip />
                            <Area type="monotone" dataKey="totalSessions" stroke="#0ea5e9" fillOpacity={1} fill="url(#colorGrowth)" />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center border-2 border-dashed border-white/5 rounded-2xl bg-white/[0.01]">
                        <TrendingUp className="w-8 h-8 text-gray-700 mb-3" />
                        <p className="text-gray-500 text-sm">Awaiting first session data...</p>
                    </div>
                )}
            </div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 text-center mt-4 italic font-light">"Tracking the river, not the individual droplets."</p>
        </section>

        {/* --- LAYER 3: COMMUNITY HEARTBEAT (MOOD) --- */}
        <section className="bg-white/[0.02] border border-white/10 rounded-3xl p-8">
            <div className="mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Heart className="w-5 h-5 text-red-500" />
                    Community Heartbeat
                </h3>
            </div>
            <div className="h-64 relative">
                {data.mood.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.mood}>
                            <defs>
                                <linearGradient id="colorMood" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="date" hide />
                            <YAxis hide />
                            <Tooltip />
                            <Area type="monotone" dataKey="avgInertia" stroke="#f43f5e" fillOpacity={1} fill="url(#colorMood)" />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center border-2 border-dashed border-white/5 rounded-2xl bg-white/[0.01]">
                        <Heart className="w-8 h-8 text-gray-700 mb-3" />
                        <p className="text-gray-500 text-sm">Resonance signals building...</p>
                    </div>
                )}
            </div>
             <p className="text-[10px] uppercase tracking-widest text-gray-500 text-center mt-4">Aggregated Emotional Inertia Index</p>
        </section>

      </div>

    </div>
  );
}
